// Location Context - Multi-location state management
// Provides current location context throughout the app

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { locationService, Location } from '../services/locationService';

interface LocationContextType {
  currentLocationId: string;
  currentLocation: Location | null;
  locations: Location[];
  setLocation: (locationId: string) => void;
  isLoading: boolean;
  checkConflict: (barberId: string, date: string, time: string, duration: number) => Promise<{
    hasConflict: boolean;
    message: string;
  }>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentLocationId, setCurrentLocationId] = useState(locationService.getCurrentLocationId());
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load locations on mount
    const loadLocations = async () => {
      try {
        const locs = await locationService.getAllLocations();
        setLocations(locs);
        const current = locs.find(l => l.id === currentLocationId);
        setCurrentLocation(current || null);
      } catch (error) {
        console.error('Failed to load locations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLocations();

    // Subscribe to location changes. M22 FIX (Jose 2026-10-29 audit): only update the ID here — do NOT
    // resolve currentLocation from `locations`, which is captured STALE (empty) by this once-run effect's
    // closure and would wrongly null the selection. The dedicated effect below resolves currentLocation
    // from the live `locations`/`currentLocationId` state whenever either changes.
    const unsubscribe = locationService.subscribe(id => {
      setCurrentLocationId(id);
    });

    return unsubscribe;
  }, []);

  // Update currentLocation when locations change
  useEffect(() => {
    const loc = locations.find(l => l.id === currentLocationId);
    setCurrentLocation(loc || null);
  }, [locations, currentLocationId]);

  const setLocation = (locationId: string) => {
    locationService.setCurrentLocation(locationId);
  };

  const checkConflict = async (barberId: string, date: string, time: string, duration: number) => {
    const result = await locationService.checkConflict(barberId, date, time, duration);
    return {
      hasConflict: result.hasConflict,
      message: result.message
    };
  };

  return (
    <LocationContext.Provider value={{
      currentLocationId,
      currentLocation,
      locations,
      setLocation,
      isLoading,
      checkConflict
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;
